import {
  Component,
  ViewChild,
  WritableSignal,
  computed,
  effect,
  inject,
  signal,
  untracked,
} from '@angular/core';

import { HttpParams, httpResource } from '@angular/common/http';
import { MatButtonModule } from '@angular/material/button';
import { DateAdapter } from '@angular/material/core';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginator, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, ParamMap, RouterModule, Router } from '@angular/router';
import { LeafletGroupComponent } from '@components/leaflet/group/group.component';
import { ListComponent } from '@components/list/list.component';
import { environment } from '@environment';
import {
  BooleanChip,
  Chip,
  GeoJSONToWKT,
  ItemListResponse,
  PlanktonOccurrence,
  StringArrayChip,
  StringChip,
  WKTChip,
  WKTToGeoJSON,
} from '@models';
import { PlanktonEventAbstractPipe, PlanktonEventTitlePipe } from '@pipes';
import { OccurrenceFilterComponent } from '../filter/filter.component';
import geojson from 'geojson';

@Component({
  selector: 'app-plankton-occurrence-list',
  templateUrl: './list.component.html',
  styleUrls: ['./list.component.scss'],
  imports: [
    LeafletGroupComponent,
    ListComponent,
    MatButtonModule,
    MatCardModule,
    MatChipsModule,
    MatIconModule,
    MatListModule,
    MatMenuModule,
    MatPaginatorModule,
    MatTooltipModule,
    PlanktonEventAbstractPipe,
    PlanktonEventTitlePipe,
    RouterModule,
  ],
})
export class PlanktonOccurrenceListComponent {
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  private dateAdapter = inject<DateAdapter<Date>>(DateAdapter);
  dialog = inject(MatDialog);
  route = inject(ActivatedRoute);
  router = inject(Router);

  planktonDatasetId = environment.planktonDatasetId;

  count = signal(0);
  page = signal(0);
  pageSize = signal(10);
  q = signal('');
  subtaxaToggle = signal(false);

  location = signal<string | undefined>(this.route.snapshot.queryParams['location']);
  geojsonData = signal<geojson.FeatureCollection | undefined>(undefined);

  singleFilters = new Map<string, WritableSignal<string>>([
    ['eventID', signal('')],
    ['fieldNumber', signal('')],
    ['from', signal('')],
    ['originID', signal('')],
    ['planktonType', signal('')],
    ['q', this.q],
    ['until', signal('')],
  ]);

  multiFilters = new Map<string, WritableSignal<string[]>>([
    ['expedition', signal<string[]>([])],
    ['gearType', signal<string[]>([])],
    ['lifeStage', signal<string[]>([])],
    ['scientificName', signal<string[]>([])],
  ]);

  httpParams = computed(() => {
    const count = untracked(() => this.count() === 0 || this.page() === 0);
    let params = new HttpParams()
      .set('count', count)
      .set('event', true)
      .set('taxon', true)
      .set('skip', this.page() * this.pageSize())
      .set('take', this.pageSize());
    if (this.subtaxaToggle()) {
      params = params.set('subtaxa', 'true');
    }
    for (const [k, sig] of this.singleFilters) {
      if (sig() != '') {
        params = params.set(k, sig());
      }
    }
    for (const [k, sig] of this.multiFilters) {
      for (const vv of sig()) {
        params = params.append(k, vv);
      }
    }
    const location = this.location();
    if (location) {
      params = params.set('location', location);
    }
    return params;
  });

  response = httpResource<ItemListResponse<PlanktonOccurrence>>(() => {
    return `${environment.kinkoHost}/plankton/occurrence/?${this.httpParams()}`;
  });

  activeChips = computed(() => {
    const p = this.httpParams();
    const chips: Chip[] = [];

    for (const [k, sig] of this.singleFilters) {
      if (p.has(k)) {
        chips.push(new StringChip(k, sig));
      }
    }
    if (p.get('subtaxa')) {
      chips.push(new BooleanChip('subtaxa', this.subtaxaToggle));
    }
    for (const [k, sig] of this.multiFilters) {
      const v = p.getAll(k);
      if (v) {
        for (const vv of v) {
          chips.push(new StringArrayChip(k, sig, vv));
        }
      }
    }
    if (this.location()) {
      chips.push(new WKTChip('location', this.location));
    }
    return chips.sort((one, other) => one.compare(other));
  });

  csvDownloadURL = computed(() => {
    const q = this.httpParams()
      .delete('skip')
      .delete('take')
      .delete('count')
      .delete('taxon')
      .set('format', 'csv');
    return `${environment.kinkoHost}/plankton/occurrence/?${q}`;
  });

  constructor() {
    this.route.queryParamMap.subscribe((pm: ParamMap) => {
      this.page.set(parseInt(pm.get('page') ?? '0'));
      this.pageSize.set(parseInt(pm.get('pageSize') ?? '10'));
      this.subtaxaToggle.set(pm.get('subtaxa') == 'true');
      for (const [k, sig] of this.singleFilters) {
        sig.set(pm.get(k) || '');
      }
      for (const [k, sig] of this.multiFilters) {
        sig.set([...(pm.getAll(k) || [])]);
      }
      this.location.set(pm.get('location') ?? undefined);
    });

    effect(() => {
      if (!this.response.hasValue() || !this.response.value().count) {
        return;
      }

      this.count.set(this.response.value().count);
    });

    effect(() => {
      const location = WKTToGeoJSON(this.location());
      this.geojsonData.set(location);
    });
  }

  onPageEvent(event: PageEvent) {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParamsHandling: 'merge',
      queryParams: {
        page: event.pageIndex || undefined,
        pageSize: event.pageSize === 10 ? undefined : event.pageSize,
      },
      replaceUrl: true,
    });
  }

  onSearchEvent(event: Event) {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParamsHandling: 'merge',
      queryParams: {
        q: (event.target as HTMLInputElement).value.trim() || undefined,
        page: undefined,
      },
      replaceUrl: true,
    });
  }

  onFilterRemove(chip: Chip) {
    chip.remove();
    this.router.navigate([], {
      relativeTo: this.route,
      queryParamsHandling: 'merge',
      queryParams: {
        [chip.key]: chip.signal() || undefined,
        page: undefined,
      },
    });
  }

  openFilterDialog() {
    const data: Record<string, boolean | string | string[] | geojson.FeatureCollection> = {};
    for (const [k, sig] of this.singleFilters) {
      data[k] = sig();
    }
    data['subtaxa'] = this.subtaxaToggle();
    for (const [k, sig] of this.multiFilters) {
      data[k] = [...sig()];
    }
    data['geojson'] = WKTToGeoJSON(this.location());

    this.dialog
      .open(OccurrenceFilterComponent, {
        data: data,
        minWidth: '360px',
        maxWidth: '960px',
      })
      .afterClosed()
      .subscribe(
        (
          data: {
            expedition: string[];
            fieldNumber: string;
            from: Date | null;
            gearType: string[];
            geojson: GeoJSON.FeatureCollection | undefined;
            lifeStage: string[];
            planktonType: string;
            q: string;
            scientificName: string[];
            subtaxa: boolean;
            until: Date | null;
          } | null,
        ) => {
          if (!data) {
            return;
          }

          const qp: Record<string, string | string[] | undefined> = {
            expedition: data.expedition || undefined,
            fieldNumber: data.fieldNumber,
            from: data.from ? this.dateAdapter.format(data.from, null) : undefined,
            gearType: data.gearType || undefined,
            lifeStage: data.lifeStage || undefined,
            location: data.geojson?.features?.length ? GeoJSONToWKT(data.geojson) : undefined,
            page: undefined,
            planktonType: data.planktonType,
            q: data.q,
            scientificName: data.scientificName || undefined,
            subtaxa: data.scientificName?.length && data.subtaxa ? 'true' : undefined,
            until: data.until ? this.dateAdapter.format(data.until, null) : undefined,
          };

          this.router.navigate([], {
            relativeTo: this.route,
            queryParamsHandling: 'merge',
            queryParams: qp,
            replaceUrl: true,
          });
        },
      );
  }
}
